import type { Candidate } from '../candidate'
import type { DerivedFacts } from '../facts'
import { RULE_IDS } from '../version'
import { evidenceFor, missingEvidenceFor, watchItemsFor } from './shared'

/**
 * Mini cut (phase-system.md, rules-engine.md).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE MOST AGGRESSIVE PHASE, AND THE ONE THAT ENDS RATHER THAN EXTENDS.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * At −1.0% to −0.7% per week (D-065) this is the fastest rate the app will ever
 * recommend, and its safety comes from being **short and explicitly bounded**.
 * Two things follow, and both are rules rather than tone:
 *
 *  - **Reaching the planned end recommends transition, never extension.**
 *    rules-engine.md: "do not extend it impulsively beyond the planned end".
 *    That is handled by `plannedEndReached` in `shared.ts`, which outranks
 *    everything here.
 *  - **Nothing in this family ever proposes a larger deficit.** Not one rule
 *    sets `reducesIntakeOrActivity`, because at this rate the only safe
 *    direction to adjust is gentler.
 */
export function evaluateMiniCut(facts: DerivedFacts): Candidate {
  const phase = facts.phase
  if (!phase || phase.ratePosition === undefined) {
    throw new Error('mini cut rules require a phase and a comparable week')
  }

  if (phase.ratePosition === 'fasterThanRange') return lossFasterThanBounded(facts)
  if (phase.ratePosition === 'insideRange') return onTrack(facts)
  return notLosingAsPlanned(facts)
}

function onTrack(facts: DerivedFacts): Candidate {
  return {
    category: 'stayTheCourse',
    primaryAction:
      'Continue as planned through to the end date you set. Your loss is inside the range for this phase, and a mini cut works by being short rather than by being pushed further.',
    supportingActions: [],
    ruleId: RULE_IDS.miniCutOnTrack,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      'A mini cut is meant to be brief. Watch sleep, hunger and training quality rather than only the scale.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Extend the phase because it is going well',
        reason:
          'The safety of a mini cut comes from its short, planned duration. Extending it is exactly what turns it into something else.',
      },
      {
        action: 'Push the deficit harder',
        reason: 'This is already the fastest rate Project Sola will recommend.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/**
 * Losing faster than even a mini cut's range allows.
 *
 * The conservative direction, always: ease. There is no branch here that
 * suggests going faster.
 */
function lossFasterThanBounded(facts: DerivedFacts): Candidate {
  return {
    category: 'easeTheCurrentPhase',
    primaryAction:
      'Ease the deficit. You are losing faster than even this phase intends, and at this rate that is where strength and recovery go first.',
    supportingActions: ['Keep protein and sleep up while the rate settles.'],
    ruleId: RULE_IDS.miniCutTooFast,
    family: 'safetyAndRecovery',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      'Losing faster than planned during a mini cut is the clearest reason to shorten or end it early.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Carry on at this rate because the phase is nearly over',
        reason:
          'A short phase does not make a rate safe. The faster the loss, the more of it comes from muscle.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/** Not losing at the planned rate — reported, never escalated. */
function notLosingAsPlanned(facts: DerivedFacts): Candidate {
  return {
    category: 'gatherMoreData',
    primaryAction:
      'Keep the plan as it is for now and keep weighing in. You are losing more slowly than this phase intends, and a mini cut is too short to judge on one week.',
    supportingActions: [],
    ruleId: RULE_IDS.miniCutStalled,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      'Water retention can hide fat loss for a week or more, particularly when training or salt changes.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Cut intake further to hit the planned rate',
        reason:
          'This phase already runs the largest deficit Project Sola will recommend. Making it larger to chase a weekly number is how a mini cut becomes unsafe.',
      },
      {
        action: 'Extend the phase to make up the difference',
        reason:
          'A mini cut ends on its planned date. Extending it is a separate, explicit decision.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}
